import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useRef, useState } from 'react';
import { PharmacyMapHandle } from 'src/features/pharmacies/shared/ui/PharmacyMap/constants';

import DiagnosisAppInstallBottomSheet from '@/components/templates/nonBenefit/home/components/DiagnosisAppInstallBottomSheet';
import { FindPharmacyDdAction, PharmacyDdAction } from '@/constants/ddActions/pharmacy';
import { OutLinks } from '@/constants/url';
import usePharmacyViewType from '@/features/pharmacies/shared/hooks/usePharmacyViewType';
import { PharmacyListBottomSheetHandle } from '@/features/pharmacies/shared/ui/PharmacyListBottomSheet/constants';
import useWebLocation from '@/hooks/common/useWebLocation';
import useFilteredAndSortedPharmacy from '@/hooks/pharmacy/useFilteredAndSortedPharmacy';
import useFindGeneralListOfNearbyPharmacyV3ByUser from '@/hooks/pharmacy/useFindGeneralListOfNearbyPharmacyV3ByUser';
import usePharmacySearchFilters from '@/hooks/pharmacy/usePharmacySearchFilters';
import useModal from '@/hooks/useModal';
import { NonBenefitMedicine } from '@/models/nonBenefit';
import { PharmacyModel } from '@/models/pharmacy';
import { Client } from '@/types/app';
import { PharmacyFilterType, PharmacySortType } from '@/types/bff/gql-schema';
import { BreadCrumb, ROUTES } from '@/types/common';
import { UserCoordinate } from '@/types/hospital';
import { MODAL_TYPE } from '@/types/modal';
import { NonBenefitMapQueries } from '@/types/nonBenefit';
import { ENearbyPharmacySearchSortType } from '@/types/pharmacy';
import Airbridge from '@/utils/airbridge';
import { NATIVE_POST_MESSAGE_TYPE } from '@/utils/mobile/RNPlugins';
import { sendPostMessageToNative } from '@/utils/mobile/RNPlugins';
import { addQueryString } from '@/utils/url';

import { DOCTOR_NOW_COMPANY_LATITUDE, DOCTOR_NOW_COMPANY_LONGITUDE } from '@/constants/map';
import { NearbyPharmacyFilterCriteria } from '@/features/pharmacies/shared/hooks/usePharmacyFilterViewModel';
import useLoginViewModel from '../login/useLoginViewModel';
import useAddFavoritePharmacy from '../pharmacy/useAddFavoritePharmacy';
import useFavoritePharmacies from '../pharmacy/useFavoritePharmacies';
import { Tab } from '../pharmacy/useFindPharmacyCategoryTabs';
import useRemoveFavoritePharmacy from '../pharmacy/useRemoveFavoritePharmacy';
import useLogger from '../useLogger';
import useToaster from '../useToaster';
import useNonBenefitMapCategoryTabs from './useNonBenefitMapCategoryTabs';

const airbrigdeLink = 'https://abr.ge/w3tbbv';

const useNonBenefitMapViewModel = (client: Client) => {
  const router = useRouter();
  const queries = router.query as NonBenefitMapQueries;
  const { treatmentMedicineId } = queries;
  const { tabs, selectedTab, selectTab } = useNonBenefitMapCategoryTabs();
  const mapRef = useRef<PharmacyMapHandle>(null);
  const pharmacyListBottomSheetRef = useRef<PharmacyListBottomSheetHandle>(null);

  const { showModal, closeModal } = useModal();

  const breadCrumbs: BreadCrumb[] = [
    {
      label: '최저가 약국 찾기',
      url: ROUTES.NON_BENEFIT.MAP.path + addQueryString(queries),
      logName: 'non_benefit_map',
    },
  ];

  const scrollListRef = useRef<HTMLDivElement>(null);
  const userLocationHook = useWebLocation();

  const [selectedPharmacy, setSelectedPharmacy] = useState<PharmacyModel | null>(null);
  const [pharmacyMapInitiateError, setPharmacyMapInitiateError] = useState<ErrorEvent | null>(null);
  const [pharmacyFilterCriteria, setPharmacyFilterCriteria] = useState<NearbyPharmacyFilterCriteria | null>(null);

  const queryClient = useQueryClient();

  const { addFavoritePharmacyMutation } = useAddFavoritePharmacy();
  const { removeFavoritePharmacyMutation } = useRemoveFavoritePharmacy();

  const { showToast } = useToaster();

  const { log } = useLogger();

  const loginViewModel = useLoginViewModel();

  const [showLoginModal, setShowLoginModal] = useState(false);

  const openLoginModal = () => {
    setShowLoginModal(true);
  };

  const closeLoginModal = () => {
    setShowLoginModal(false);
  };

  const shouldShowPharmacyCard = !!selectedPharmacy;

  const updateSelectedPharmacy = (pharmacy: PharmacyModel) => {
    setSelectedPharmacy(pharmacy);
    if (pharmacyListBottomSheetRef.current) {
      pharmacyListBottomSheetRef.current?.setInitialSnapPoint();
    }
  };

  const resetSelectedPharmacy = () => {
    setSelectedPharmacy(null);
    if (pharmacyListBottomSheetRef.current) {
      pharmacyListBottomSheetRef.current?.setInitialSnapPoint();
    }
  };

  const updatePharmacyMapInitiateError = (error: ErrorEvent) => {
    setPharmacyMapInitiateError(error);
  };

  const pharmacyViewTypeHook = usePharmacyViewType();

  const selectedTreatmentMedicine = pharmacyFilterCriteria?.selectedMedicine;

  const searchFiltersHook = usePharmacySearchFilters({
    nearbyPharmacySortsAndFilters: {
      filters: pharmacyFilterCriteria?.selectedTab?.filters ?? [],
      sorts: pharmacyFilterCriteria?.selectedTab?.sortTypes ?? [],
    },
    defaultSortType: pharmacyFilterCriteria?.selectedTab?.sortTypes[0].type ?? ENearbyPharmacySearchSortType.LowPrice,
  });

  const { data: rawPharmacyList, isLoading: isNearbyPharmaciesLoading } = useFindGeneralListOfNearbyPharmacyV3ByUser({
    ...searchFiltersHook.getBoundaryEastWestLongitudeAndLatitude(!!pharmacyMapInitiateError),
    latitude: searchFiltersHook.mapCoordinates.latitude,
    longitude: searchFiltersHook.mapCoordinates.longitude,
    diagnosisType: pharmacyFilterCriteria?.selectedTab?.diagnosisType,
    diagnosisTypeId: pharmacyFilterCriteria?.selectedTab?.diagnosisTypeId,
    treatmentMedicineId: pharmacyFilterCriteria?.selectedMedicine?.treatmentMedicine.treatmentMedicineId,
    dispensationUnit: pharmacyFilterCriteria?.selectedMedicine?.dispensationUnit.dispensationUnit,
    shouldIncludeDiagnosisPrice:
      pharmacyFilterCriteria?.includeDiagnosisPriceFilter?.isSelectedFilter(
        pharmacyFilterCriteria?.selectedCommonFilters ?? []
      ) ?? false, // TODO. NearbyPharmacyFilterCriteria 를 클래스로 변경하고 train wreck 없애야
  });

  const { refinedPharmacy: pharmacies } = useFilteredAndSortedPharmacy({
    filters: (pharmacyFilterCriteria?.selectedCommonFilters ?? []) as unknown as PharmacyFilterType[], // TODO. 왜 이런식으로 캐스팅을? 변경하기
    sortType: pharmacyFilterCriteria?.selectedSortMethod.type as unknown as PharmacySortType,
    rawPharmacyData: rawPharmacyList ?? [],
    shouldFilterByPriceMedicine: !!pharmacyFilterCriteria?.selectedMedicine?.treatmentMedicine.treatmentMedicineId, // TODO. NearbyPharmacyFilterCriteria 를 클래스로 변경하고 train wreck 없애야
  });

  const shouldShowEmptyPharmacyMessage = () => {
    if (isNearbyPharmaciesLoading || pharmacies.length) {
      return false;
    }

    if (pharmacies.length === 0) {
      return true;
    }

    return false;
  };

  const parseSelectNonBenefitMedicine = (): NonBenefitMedicine => {
    const defaultValue = {
      // 선택된 약 아이디가 없으면 서버사이드 props로 내려온 쿼리값으로 초기화
      id: pharmacyFilterCriteria?.selectedMedicine?.treatmentMedicine.treatmentMedicineId || treatmentMedicineId || '',
      medicineName: pharmacyFilterCriteria?.selectedMedicine?.medicineDisplayName() ?? '',
      medicineDescription: '',
      // 사실상 사용하는곳 없음, 일단 NonBenefitMedicine 타입에 맞게 넣어둠, 서버에서도 모두 true로 내려옴, 추후 삭제해도 될듯
      units: [],
      treatment: {
        // 처음에는 선택된 탭에 있는 진료 아이디로 주고, 선택된 약 아이디가 없으면 서버사이드 props로 내려온 쿼리값으로 초기화
        id: pharmacyFilterCriteria?.selectedTab?.diagnosisTypeId || treatmentMedicineId || '',
        // NonBenefitMedicine 클래스에 name 사용하는 부분 없어서 빈값으로 고정, 추후 삭제해도 될듯
        name: '',
        isBenefit: false,
        // 사용하는곳 없음, 추후 삭제해도 될듯
        categoryId: '',
      },
    };

    return new NonBenefitMedicine(defaultValue);
  };

  const onClickViewListModeButtonWithResetSelectedPharmacy = () => {
    resetSelectedPharmacy();
    pharmacyListBottomSheetRef.current?.clickViewListModeButton();
  };

  const selectedMedicine: NonBenefitMedicine = parseSelectNonBenefitMedicine();

  const onClickPharmacyItem = (selectPharmacy: PharmacyModel) => {
    if (pharmacyMapInitiateError) {
      return;
    }
    setSelectedPharmacy(selectPharmacy);
  };

  const scrollToTop = () => {
    scrollListRef.current?.scrollTo({ top: 0 });
  };

  const applyFilter = (filterCriteria: NearbyPharmacyFilterCriteria) => {
    scrollToTop();
    setPharmacyFilterCriteria(filterCriteria);
  };

  const isSelectedLowPrice = !!pharmacyFilterCriteria?.selectedMedicine;

  const [currentMapLocation, setCurrentMapLocation] = useState<{ latitude: number; longitude: number }>({
    latitude: DOCTOR_NOW_COMPANY_LATITUDE,
    longitude: DOCTOR_NOW_COMPANY_LONGITUDE,
  });

  const updateCurrentMapCoordinate = (southWest: UserCoordinate, northEast: UserCoordinate) => {
    searchFiltersHook.updateMapCoordinate(southWest, northEast);

    const mapCenterLatLng = mapRef.current?.getCenterLatLng();

    const latitude = mapCenterLatLng?.latitude;
    const longitude = mapCenterLatLng?.longitude;

    if (!latitude || !longitude) {
      return;
    }

    setCurrentMapLocation({
      latitude,
      longitude,
    });
  };

  const isSelectedAllTab = () => {
    // 전체 탭의 세부 정보 (서버에서 내려옴)
    const ALL_TAB_INFO = {
      name: '전체',
      diagnosisTypeId: null,
      diagnosisType: null,
    };

    const isSameDiagnosisTypeWithAllTab =
      pharmacyFilterCriteria?.selectedTab?.diagnosisType === ALL_TAB_INFO.diagnosisType;
    const isSameDiagnosisTypeIdWithAllTab =
      pharmacyFilterCriteria?.selectedTab?.diagnosisTypeId === ALL_TAB_INFO.diagnosisTypeId;
    const isSameNameWithAllTab = pharmacyFilterCriteria?.selectedTab?.name === ALL_TAB_INFO.name;

    return (isSameDiagnosisTypeWithAllTab && isSameDiagnosisTypeIdWithAllTab && isSameNameWithAllTab) ?? false;
  };

  const getFavoritePharmacyClickEventName = (isFavorite: boolean) => {
    const viewType = pharmacyViewTypeHook.shouldShowListView ? 'list' : 'map';
    if (isFavorite) {
      return `${PharmacyDdAction.ClickRemoveFavoritePharmacy}_find_${viewType}`;
    }
    return `${PharmacyDdAction.ClickAddFavoritePharmacy}_find_${viewType}`;
  };

  const onClickOpenAppInstallLink = () => {
    Airbridge.setDeepLink({
      buttonID: 'pharmacy-map',
      deeplinkUrl: airbrigdeLink,
      ctaParams: {
        cta_param_1: '최저가 약국 지도',
        cta_param_2: selectedMedicine.medicineName,
        cta_param_3: selectedMedicine.treatment.name,
      },
    });
  };

  const onOpenAppInstallBottomSheet = () => {
    showModal({
      type: MODAL_TYPE.BASIC,
      props: {
        body: (
          <DiagnosisAppInstallBottomSheet
            client={client}
            buttonId={'pharmacy-map'}
            title={`닥터나우 앱을 설치하면\n${selectedMedicine.treatmentProductMedicineName} 처방이 가능해요!`}
            description={'QR 코드를 스캔하면 앱을 설치할 수 있습니다'}
            onClose={closeModal}
            qrCodeUrl={airbrigdeLink}
            onClickOpenAppInstallLink={onClickOpenAppInstallLink}
          />
        ),
      },
    });
  };

  const onClickTab = (tab: Tab) => {
    selectTab(tab);
    resetSelectedPharmacy();
  };

  const addFavoritePharmacy = (pharmacy: PharmacyModel) => {
    addFavoritePharmacyMutation(
      {
        pharmacyId: pharmacy.pharmacyId,
      },
      {
        onSuccess: () => {
          showToast('단골 약국으로 지정했어요.\n더보기 메뉴에서 확인할 수 있어요.');
          pharmacy.updateFavoritePharmacy(true);
          // 단골 약국 캐시 업데이트
          useFavoritePharmacies.updateFavoritePharmacyStatus(queryClient, pharmacy.pharmacyId, true);
        },
        onError: (error) => {
          showToast(error.message);
        },
      }
    );
  };

  const removeFavoritePharmacy = (pharmacy: PharmacyModel) => {
    removeFavoritePharmacyMutation(
      {
        pharmacyId: pharmacy.pharmacyId,
      },
      {
        onSuccess: () => {
          showToast('단골 약국에서 제외했어요.');
          pharmacy.updateFavoritePharmacy(false);
          // 단골 약국 캐시 업데이트
          useFavoritePharmacies.updateFavoritePharmacyStatus(queryClient, pharmacy.pharmacyId, false);
        },
      }
    );
  };

  const onClickFavoritePharmacy = (e: React.MouseEvent, pharmacy: PharmacyModel) => {
    e.stopPropagation();

    if (!client?.isWebView && !loginViewModel.isLoggedIn) {
      openLoginModal();
      return;
    }

    if (pharmacy.isFavoritePharmacy) {
      return removeFavoritePharmacy(pharmacy);
    }
    addFavoritePharmacy(pharmacy);
  };

  const openNowPharmacyInfo = () => {
    log({ eventName: `${FindPharmacyDdAction.ClickNowInfoInFind}` });

    sendPostMessageToNative(NATIVE_POST_MESSAGE_TYPE.MOVE_TO_PAGE, {
      screenName: 'PureWebViewScreen',
      params: {
        uri: OutLinks.NowPharmacyInfo,
        headerTitle: '',
      },
    });
  };

  const onClickCurrentLocation = async () => {
    // useWebLocation을 이용한 구현
    userLocationHook.requestGeolocationPermissionInWeb(({ latitude, longitude }) => {
      if (pharmacyMapInitiateError) {
        return;
      }

      mapRef.current?.setCenter(latitude, longitude);
    }, true);
  };

  return {
    breadCrumbs,
    ...userLocationHook,
    ...searchFiltersHook,
    isNearbyPharmaciesLoading,
    pharmacies,
    scrollListRef,
    selectedTreatmentMedicine,
    isSelectedLowPrice,
    queries,
    selectedMedicine,
    currentMapLocation,
    selectedPharmacy,
    mapRef,
    pharmacyListBottomSheetRef,
    tabs,
    selectedTab,
    selectedTabName: selectedTab?.name ?? '탈모약',
    shouldShowPharmacyCard,
    selectTab,
    onClickTab,
    shouldShowEmptyPharmacyMessage,
    updateCurrentMapCoordinate,
    applyFilter,
    updateSelectedPharmacy,
    resetSelectedPharmacy,
    updatePharmacyMapInitiateError,
    onClickPharmacyItem,
    isSelectedAllTab,
    onOpenAppInstallBottomSheet,
    getFavoritePharmacyClickEventName,
    onClickViewListModeButtonWithResetSelectedPharmacy,
    openNowPharmacyInfo,
    onClickFavoritePharmacy,
    openLoginModal,
    closeLoginModal,
    showLoginModal,
    onClickCurrentLocation,
  };
};

export default useNonBenefitMapViewModel;
